import {
    Box, Typography, Button, Paper, Alert, CircularProgress, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, TextField, Tooltip, Checkbox,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import themeColors from "../theme";
import EmailIcon from "@mui/icons-material/Email";
import PeopleIcon from "@mui/icons-material/People";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import { fetchBookPriceStatus, triggerPriceRefresh, updateBookPrice } from "../api/bookPricesApi";
import {
    fetchEquipmentPriceStatus,
    triggerEquipmentPriceRefresh,
    updateEquipmentPrice,
} from "../api/equipmentPricesApi";
import {
    fetchBookEditionStatus,
    triggerEditionRefresh,
    dismissEditionSuggestion,
    acknowledgeEditionSuggestion,
} from "../api/bookEditionsApi";
import { useAuth } from "../context/AuthContext";
import PromoCodesSection from "../components/PromoCodesSection";
import CmeRequirementNotifications from "../components/CmeRequirementNotifications";
import MarketplaceNotifications from "../components/MarketplaceNotifications";
import ClinicalUpdateNotifications from "../components/ClinicalUpdateNotifications";
import medicalEquipmentData from "../data/medicalEquipmentData.json";

// Display title follows the marketplace's "Category (Brand - Model)" format.
const equipmentTitleById = Object.fromEntries(
    (medicalEquipmentData.equipment || []).map((item) => {
        const brandModel = [item.brand, item.model].filter(Boolean).join(" - ");
        return [item.id, brandModel ? `${item.category} (${brandModel})` : item.category];
    })
);

export default function AdminPage() {
    const navigate = useNavigate();
    const { token } = useAuth();
    const { teal, lightGray } = themeColors;
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    // Book price state
    const [priceStatus, setPriceStatus] = useState(null);
    const [priceLoading, setPriceLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshMsg, setRefreshMsg] = useState(null);
    const [refreshErr, setRefreshErr] = useState(null);

    // Pagination & filter state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filter, setFilter] = useState("all"); // "all" | "priced" | "failed"

    // Inline edit state
    const [editingBookId, setEditingBookId] = useState(null);
    const [editPrice, setEditPrice] = useState("");

    // Equipment price state
    const [equipStatus, setEquipStatus] = useState(null);
    const [equipLoading, setEquipLoading] = useState(true);
    const [equipRefreshing, setEquipRefreshing] = useState(false);
    const [equipRefreshMsg, setEquipRefreshMsg] = useState(null);
    const [equipRefreshErr, setEquipRefreshErr] = useState(null);
    const [equipPage, setEquipPage] = useState(0);
    const [equipRowsPerPage, setEquipRowsPerPage] = useState(10);
    const [equipFilter, setEquipFilter] = useState("all"); // "all" | "priced" | "failed"
    const [editingEquipmentId, setEditingEquipmentId] = useState(null);
    const [editEquipPrice, setEditEquipPrice] = useState("");

    // Book edition state
    const [editionStatus, setEditionStatus] = useState(null);
    const [editionLoading, setEditionLoading] = useState(true);
    const [editionRefreshing, setEditionRefreshing] = useState(false);
    const [editionMsg, setEditionMsg] = useState(null);
    const [editionErr, setEditionErr] = useState(null);
    const [showAllEditions, setShowAllEditions] = useState(false);

    useEffect(() => {
        fetchBookPriceStatus(token)
            .then((data) => setPriceStatus(data))
            .catch(() => setPriceStatus(null))
            .finally(() => setPriceLoading(false));

        fetchBookEditionStatus(token)
            .then((data) => setEditionStatus(data))
            .catch(() => setEditionStatus(null))
            .finally(() => setEditionLoading(false));

        fetchEquipmentPriceStatus(token)
            .then((data) => setEquipStatus(data))
            .catch(() => setEquipStatus(null))
            .finally(() => setEquipLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRefreshEditions = async () => {
        setEditionRefreshing(true);
        setEditionMsg(null);
        setEditionErr(null);
        try {
            const data = await triggerEditionRefresh(token);
            setEditionMsg(data.message);
        } catch (err) {
            setEditionErr(err.response?.data?.message || "Failed to start the edition check.");
        } finally {
            setEditionRefreshing(false);
        }
    };

    const handleDismissEdition = async (bookId, asin) => {
        try {
            await dismissEditionSuggestion(bookId, asin, token);
            setEditionStatus((prev) => {
                const editions = prev.editions.map((e) =>
                    e.bookId === bookId ? { ...e, newerAvailable: false } : e
                );
                return {
                    ...prev,
                    editions,
                    summary: {
                        ...prev.summary,
                        newerAvailable: editions.filter((e) => e.newerAvailable).length,
                    },
                };
            });
        } catch {
            // silently fail — admin can retry
        }
    };

    const handleAcknowledgeEdition = async (bookId, asin) => {
        try {
            await acknowledgeEditionSuggestion(bookId, asin, token);
            setEditionStatus((prev) => {
                const editions = prev.editions.map((e) =>
                    e.bookId === bookId ? { ...e, newerAvailable: false } : e
                );
                return {
                    ...prev,
                    editions,
                    summary: {
                        ...prev.summary,
                        newerAvailable: editions.filter((e) => e.newerAvailable).length,
                    },
                };
            });
        } catch {
            // Leave the suggestion visible so the admin can retry.
        }
    };

    // Books with a newer edition first; the rest only when expanded.
    const flaggedEditions = (editionStatus?.editions || []).filter((e) => e.newerAvailable);
    const visibleEditions = showAllEditions
        ? (editionStatus?.editions || [])
        : flaggedEditions;

    const handleRefreshPrices = async () => {
        setRefreshing(true);
        setRefreshMsg(null);
        setRefreshErr(null);
        try {
            const data = await triggerPriceRefresh(token);
            setRefreshMsg(data.message);
        } catch (err) {
            setRefreshErr(err.response?.data?.message || "Failed to trigger price refresh.");
        } finally {
            setRefreshing(false);
        }
    };

    // Filtered prices based on active chip
    const filteredPrices = (priceStatus?.prices || []).filter((p) => {
        if (filter === "priced") return p.price != null;
        if (filter === "failed") return p.fetchError != null;
        return true;
    });

    const handleSavePrice = async (bookId) => {
        const value = parseFloat(editPrice);
        if (isNaN(value) || value < 0) return;
        try {
            const updated = await updateBookPrice(bookId, value, token);
            setPriceStatus((prev) => {
                const newPrices = prev.prices.map((p) =>
                    p.bookId === bookId
                        ? {
                            ...p,
                            price: updated.price,
                            fetchError: null,
                            lastSuccessAt: new Date().toISOString(),
                        }
                        : p
                );
                const withPrice = newPrices.filter((p) => p.price != null).length;
                const withError = newPrices.filter((p) => p.fetchError != null).length;
                return {
                    ...prev,
                    prices: newPrices,
                    summary: { ...prev.summary, withPrice, withError },
                };
            });
        } catch {
            // silently fail — user can retry
        }
        setEditingBookId(null);
        setEditPrice("");
    };

    const handleRefreshEquipmentPrices = async () => {
        setEquipRefreshing(true);
        setEquipRefreshMsg(null);
        setEquipRefreshErr(null);
        try {
            const data = await triggerEquipmentPriceRefresh(token);
            setEquipRefreshMsg(data.message);
        } catch (err) {
            setEquipRefreshErr(err.response?.data?.message || "Failed to trigger price refresh.");
        } finally {
            setEquipRefreshing(false);
        }
    };

    // Filtered equipment prices based on active chip
    const filteredEquipPrices = (equipStatus?.prices || []).filter((p) => {
        if (equipFilter === "priced") return p.price != null;
        if (equipFilter === "failed") return p.fetchError != null;
        return true;
    });

    const handleSaveEquipPrice = async (equipmentId) => {
        const value = parseFloat(editEquipPrice);
        if (isNaN(value) || value < 0) return;
        try {
            const updated = await updateEquipmentPrice(equipmentId, value, token);
            setEquipStatus((prev) => {
                const newPrices = prev.prices.map((p) =>
                    p.equipmentId === equipmentId
                        ? {
                            ...p,
                            price: updated.price,
                            fetchError: null,
                            lastSuccessAt: new Date().toISOString(),
                        }
                        : p
                );
                const withPrice = newPrices.filter((p) => p.price != null).length;
                const withError = newPrices.filter((p) => p.fetchError != null).length;
                return {
                    ...prev,
                    prices: newPrices,
                    summary: { ...prev.summary, withPrice, withError },
                };
            });
        } catch {
            // silently fail — user can retry
        }
        setEditingEquipmentId(null);
        setEditEquipPrice("");
    };

    const handleSendTestEmail = async () => {
        setSending(true);
        setMessage(null);
        setError(null);
        try {
            await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/api/admin/test-email`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessage("Welcome email sent successfully. Check the inbox of the logged-in admin.");
        } catch (err) {
            setError(err.response?.data?.error || "Failed to send welcome email.");
        } finally {
            setSending(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: lightGray }}>
            <HeaderBar />
            <Box sx={{ px: { xs: 2, md: 8 }, py: 6, maxWidth: "800px", mx: "auto" }}>
                <Typography variant="h4" fontWeight="bold" color="#2C3E50" mb={1}>
                    Admin
                </Typography>
                <Typography variant="body1" color="#6C757D" mb={4}>
                    Admin-only tools and settings.
                </Typography>

                {/* Test Emails - send actual Welcome email to logged-in admin */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" color={teal} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <EmailIcon /> Test emails
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Send the actual Welcome email (the one we send after registration) to the currently logged-in admin. It will use your name and email in the placeholders.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleSendTestEmail}
                        disabled={sending}
                        startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
                        sx={{ backgroundColor: teal, "&:hover": { backgroundColor: themeColors.darkTeal } }}
                    >
                        {sending ? "Sending…" : "Send Welcome email to me"}
                    </Button>
                    {message && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            {message}
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </Paper>

                <ClinicalUpdateNotifications />

                <CmeRequirementNotifications />
                <MarketplaceNotifications />

                {/* Promo codes - trial-extension codes for residency grads and campaigns */}
                <PromoCodesSection />

                {/* Registered Users link */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" color={teal} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PeopleIcon /> Registered users
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        View all registered users and their profile information.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => navigate("/admin/users")}
                        startIcon={<PeopleIcon />}
                        sx={{ borderColor: teal, color: teal, "&:hover": { borderColor: themeColors.darkTeal, backgroundColor: "rgba(0,127,126,0.04)" } }}
                    >
                        View registered users
                    </Button>
                </Paper>

                {/* Book Prices */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" color={teal} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MenuBookIcon /> Book prices
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Amazon book prices are scraped nightly at midnight. You can also trigger a manual refresh below.
                    </Typography>

                    {/* Summary chips */}
                    {priceLoading ? (
                        <CircularProgress size={24} />
                    ) : priceStatus ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            <Chip
                                label={`Last fetched: ${priceStatus.summary.lastFetchedAt ? new Date(priceStatus.summary.lastFetchedAt).toLocaleString() : "Never"}`}
                                size="small"
                                variant="outlined"
                            />
                            <Chip
                                label={`${priceStatus.summary.withPrice} priced`}
                                size="small"
                                color="success"
                                variant={filter === "priced" ? "filled" : "outlined"}
                                onClick={() => { setFilter(filter === "priced" ? "all" : "priced"); setPage(0); }}
                                sx={{ cursor: "pointer" }}
                            />
                            <Chip
                                label={`${priceStatus.summary.withError} scrape errors`}
                                size="small"
                                color={priceStatus.summary.withError > 0 ? "warning" : "default"}
                                variant={filter === "failed" ? "filled" : "outlined"}
                                onClick={() => { setFilter(filter === "failed" ? "all" : "failed"); setPage(0); }}
                                sx={{ cursor: "pointer" }}
                            />
                            <Chip
                                label={`${priceStatus.summary.totalBooks} total`}
                                size="small"
                                variant={filter === "all" ? "filled" : "outlined"}
                                onClick={() => { setFilter("all"); setPage(0); }}
                                sx={{ cursor: "pointer" }}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            No price data yet. Trigger a refresh to populate.
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleRefreshPrices}
                        disabled={refreshing}
                        startIcon={refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                        sx={{ backgroundColor: teal, "&:hover": { backgroundColor: themeColors.darkTeal }, mb: 2 }}
                    >
                        {refreshing ? "Starting…" : "Refresh prices now"}
                    </Button>
                    {refreshMsg && <Alert severity="info" sx={{ mb: 2 }}>{refreshMsg}</Alert>}
                    {refreshErr && <Alert severity="error" sx={{ mb: 2 }}>{refreshErr}</Alert>}

                    {/* Price table */}
                    {filteredPrices.length > 0 && (
                        <>
                            <TableContainer sx={{ mt: 1 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>ASIN</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Last Fetched</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredPrices
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((p) => (
                                            <TableRow key={p.bookId}>
                                                <TableCell>{p.bookId}</TableCell>
                                                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{p.asin}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        color: p.price == null
                                                            ? "text.secondary"
                                                            : p.fetchError
                                                                ? "warning.main"
                                                                : "green",
                                                    }}
                                                >
                                                    {editingBookId === p.bookId ? (
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={editPrice}
                                                                onChange={(e) => setEditPrice(e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === "Enter") handleSavePrice(p.bookId); if (e.key === "Escape") { setEditingBookId(null); setEditPrice(""); } }}
                                                                autoFocus
                                                                sx={{ width: 90 }}
                                                                inputProps={{ min: 0, step: "0.01" }}
                                                            />
                                                            <IconButton size="small" color="success" onClick={() => handleSavePrice(p.bookId)}>
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => { setEditingBookId(null); setEditPrice(""); }}>
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                            {p.price != null ? `$${p.price.toFixed(2)}` : "—"}
                                                            {p.price != null && p.fetchError && (
                                                                <Tooltip
                                                                    title={`Last confirmed ${p.lastSuccessAt ? new Date(p.lastSuccessAt).toLocaleString() : "unknown"} — showing the last known good price`}
                                                                >
                                                                    <Chip label="stale" size="small" color="warning" variant="outlined" />
                                                                </Tooltip>
                                                            )}
                                                            {p.fetchError && (
                                                                <Tooltip title="Set price manually">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => { setEditingBookId(p.bookId); setEditPrice(p.price != null ? p.price.toFixed(2) : ""); }}
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem" }}>
                                                    {p.lastFetchedAt ? new Date(p.lastFetchedAt).toLocaleString() : "—"}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem", color: "error.main" }}>
                                                    {p.fetchError || "—"}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={filteredPrices.length}
                                page={page}
                                onPageChange={(_, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[5, 10, 25]}
                            />
                        </>
                    )}
                </Paper>

                {/* Book editions - flag books with a newer edition on Amazon */}
                <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" color={teal} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <NewReleasesIcon /> Book editions
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Amazon is searched monthly for newer editions of the books we list. Anything flagged
                        below is a suggestion — open it on Amazon to confirm before updating the catalogue.
                    </Typography>

                    {editionLoading ? (
                        <CircularProgress size={24} />
                    ) : editionStatus ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            <Chip
                                label={`Last checked: ${editionStatus.summary.lastCheckedAt ? new Date(editionStatus.summary.lastCheckedAt).toLocaleString() : "Never"}`}
                                size="small"
                                variant="outlined"
                            />
                            <Chip
                                label={`${editionStatus.summary.newerAvailable} newer edition${editionStatus.summary.newerAvailable === 1 ? "" : "s"}`}
                                size="small"
                                color={editionStatus.summary.newerAvailable > 0 ? "warning" : "success"}
                            />
                            <Chip
                                label={`${editionStatus.summary.totalBooks} checked`}
                                size="small"
                                variant="outlined"
                            />
                            {editionStatus.summary.withError > 0 && (
                                <Chip
                                    label={`${editionStatus.summary.withError} check errors`}
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            No edition data yet. Run a check to populate.
                        </Typography>
                    )}

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleRefreshEditions}
                            disabled={editionRefreshing}
                            startIcon={editionRefreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                            sx={{ backgroundColor: teal, "&:hover": { backgroundColor: themeColors.darkTeal } }}
                        >
                            {editionRefreshing ? "Starting…" : "Check editions now"}
                        </Button>
                        {editionStatus?.editions?.length > 0 && (
                            <Button variant="outlined" onClick={() => setShowAllEditions(!showAllEditions)}>
                                {showAllEditions ? "Show only flagged" : "Show all books"}
                            </Button>
                        )}
                    </Box>
                    {editionMsg && <Alert severity="info" sx={{ mb: 2 }}>{editionMsg}</Alert>}
                    {editionErr && <Alert severity="error" sx={{ mb: 2 }}>{editionErr}</Alert>}

                    {!editionLoading && editionStatus && flaggedEditions.length === 0 && !showAllEditions && (
                        <Alert severity="success">Every book is on its latest known edition.</Alert>
                    )}

                    {visibleEditions.length > 0 && (
                        <TableContainer sx={{ mt: 1 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Book</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Listed</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Found</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Suggested listing</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleEditions.map((e) => (
                                        <TableRow key={e.bookId}>
                                            <TableCell sx={{ fontSize: "0.85rem", maxWidth: 260 }}>
                                                {e.title}
                                                {e.checkError && (
                                                    <Typography variant="caption" display="block" color="error.main">
                                                        {e.checkError}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{e.currentEdition ?? "—"}</TableCell>
                                            <TableCell sx={{ color: e.newerAvailable ? "warning.main" : "text.secondary", fontWeight: e.newerAvailable ? 600 : 400 }}>
                                                {e.latestEdition ?? "—"}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "0.8rem", maxWidth: 280 }}>
                                                {e.newerAvailable ? e.latestTitle : "—"}
                                            </TableCell>
                                            <TableCell>
                                                {e.newerAvailable && (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        <Tooltip title="Open on Amazon">
                                                            <IconButton size="small" component="a" href={e.latestLink} target="_blank" rel="noopener noreferrer">
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Acknowledge new edition">
                                                            <Checkbox
                                                                size="small"
                                                                onChange={() => handleAcknowledgeEdition(e.bookId, e.latestAsin)}
                                                                inputProps={{ "aria-label": "Acknowledge new edition" }}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title="Ignore this ISBN permanently">
                                                            <IconButton size="small" onClick={() => handleDismissEdition(e.bookId, e.latestAsin)}>
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>

                {/* Equipment Prices */}
                <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" color={teal} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MedicalServicesIcon /> Equipment prices
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Amazon prices for medical equipment are scraped nightly at 2 AM. You can also trigger a manual refresh below.
                    </Typography>

                    {/* Summary chips */}
                    {equipLoading ? (
                        <CircularProgress size={24} />
                    ) : equipStatus ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            <Chip
                                label={`Last fetched: ${equipStatus.summary.lastFetchedAt ? new Date(equipStatus.summary.lastFetchedAt).toLocaleString() : "Never"}`}
                                size="small"
                                variant="outlined"
                            />
                            <Chip
                                label={`${equipStatus.summary.withPrice} priced`}
                                size="small"
                                color="success"
                                variant={equipFilter === "priced" ? "filled" : "outlined"}
                                onClick={() => { setEquipFilter(equipFilter === "priced" ? "all" : "priced"); setEquipPage(0); }}
                                sx={{ cursor: "pointer" }}
                            />
                            <Chip
                                label={`${equipStatus.summary.withError} scrape errors`}
                                size="small"
                                color={equipStatus.summary.withError > 0 ? "warning" : "default"}
                                variant={equipFilter === "failed" ? "filled" : "outlined"}
                                onClick={() => { setEquipFilter(equipFilter === "failed" ? "all" : "failed"); setEquipPage(0); }}
                                sx={{ cursor: "pointer" }}
                            />
                            <Chip
                                label={`${equipStatus.summary.totalItems} total`}
                                size="small"
                                variant={equipFilter === "all" ? "filled" : "outlined"}
                                onClick={() => { setEquipFilter("all"); setEquipPage(0); }}
                                sx={{ cursor: "pointer" }}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            No price data yet. Trigger a refresh to populate.
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleRefreshEquipmentPrices}
                        disabled={equipRefreshing}
                        startIcon={equipRefreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                        sx={{ backgroundColor: teal, "&:hover": { backgroundColor: themeColors.darkTeal }, mb: 2 }}
                    >
                        {equipRefreshing ? "Starting…" : "Refresh prices now"}
                    </Button>
                    {equipRefreshMsg && <Alert severity="info" sx={{ mb: 2 }}>{equipRefreshMsg}</Alert>}
                    {equipRefreshErr && <Alert severity="error" sx={{ mb: 2 }}>{equipRefreshErr}</Alert>}

                    {/* Price table */}
                    {filteredEquipPrices.length > 0 && (
                        <>
                            <TableContainer sx={{ mt: 1 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>ASIN</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Last Fetched</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredEquipPrices
                                            .slice(equipPage * equipRowsPerPage, equipPage * equipRowsPerPage + equipRowsPerPage)
                                            .map((p) => (
                                            <TableRow key={p.equipmentId}>
                                                <TableCell sx={{ fontSize: "0.85rem", maxWidth: 260 }}>
                                                    {equipmentTitleById[p.equipmentId] || p.equipmentId}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{p.asin}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        color: p.price == null
                                                            ? "text.secondary"
                                                            : p.fetchError
                                                                ? "warning.main"
                                                                : "green",
                                                    }}
                                                >
                                                    {editingEquipmentId === p.equipmentId ? (
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={editEquipPrice}
                                                                onChange={(e) => setEditEquipPrice(e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === "Enter") handleSaveEquipPrice(p.equipmentId); if (e.key === "Escape") { setEditingEquipmentId(null); setEditEquipPrice(""); } }}
                                                                autoFocus
                                                                sx={{ width: 90 }}
                                                                inputProps={{ min: 0, step: "0.01" }}
                                                            />
                                                            <IconButton size="small" color="success" onClick={() => handleSaveEquipPrice(p.equipmentId)}>
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => { setEditingEquipmentId(null); setEditEquipPrice(""); }}>
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                            {p.price != null ? `$${p.price.toFixed(2)}` : "—"}
                                                            {p.price != null && p.fetchError && (
                                                                <Tooltip
                                                                    title={`Last confirmed ${p.lastSuccessAt ? new Date(p.lastSuccessAt).toLocaleString() : "unknown"} — showing the last known good price`}
                                                                >
                                                                    <Chip label="stale" size="small" color="warning" variant="outlined" />
                                                                </Tooltip>
                                                            )}
                                                            {p.fetchError && (
                                                                <Tooltip title="Set price manually">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => { setEditingEquipmentId(p.equipmentId); setEditEquipPrice(p.price != null ? p.price.toFixed(2) : ""); }}
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem" }}>
                                                    {p.lastFetchedAt ? new Date(p.lastFetchedAt).toLocaleString() : "—"}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem", color: "error.main" }}>
                                                    {p.fetchError || "—"}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={filteredEquipPrices.length}
                                page={equipPage}
                                onPageChange={(_, newPage) => setEquipPage(newPage)}
                                rowsPerPage={equipRowsPerPage}
                                onRowsPerPageChange={(e) => { setEquipRowsPerPage(parseInt(e.target.value, 10)); setEquipPage(0); }}
                                rowsPerPageOptions={[5, 10, 25]}
                            />
                        </>
                    )}
                </Paper>
            </Box>
        </Box>
    );
}
